import React, { useEffect, useState } from 'react';
import { Card, Table, Typography, Tag, Spin, Empty, Space, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import tenderDashboardApis from '../../api/tenderDashboard/tenderDashboardApis';

const { Title, Text, Link } = Typography;

interface Tender {
  id: string;
  tenderNumber: string;
  title: string;
  tenderFor: string;
  endDate: string;
  responses: number;
  daysToClose: number;
  daysToCloseText?: string;
  urgencyFlag?: 'RED' | 'ORANGE' | 'GREEN' | string;
}

const LiveTendersLowResponses: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const orgId = currentUser?.orgid;

  const [data, setData] = useState<Tender[] | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await tenderDashboardApis.getLiveTendersLowResponses({
        orgIds: String(orgId),
      });
      const rows: any[] = res?.data?.data || [];
      const mapped: Tender[] = rows.map((r) => ({
        id: String(r?.tenderId ?? ''),
        tenderNumber: r?.tenderNumber ?? '',
        title: r?.tenderTitle ?? '',
        tenderFor: r?.tenderFor ?? '',
        endDate: r?.endDate
          ? dayjs(r.endDate).format('DD-MMM-YYYY')
          : '',
        responses: r?.responses ?? 0,
        daysToClose: r?.daysRemaining ?? 0,
        daysToCloseText: r?.daysToClose,
        urgencyFlag: r?.urgencyFlag,
      }));
      setData(mapped);
    } catch (e) {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const getDaysToCloseTag = (record: Tender) => {
    const days = record.daysToClose;
    const text = record.daysToCloseText
      ? record.daysToCloseText
      : `${days} days left`;
    let color = 'green';
    if (record.urgencyFlag === 'RED' || days <= 3) color = 'red';
    else if (record.urgencyFlag === 'ORANGE' || days <= 7) color = 'orange';
    return <Tag color={color}>{text}</Tag>;
  };

  const columns: ColumnsType<Tender> = [
    {
      title: 'Tender ID',
      dataIndex: 'tenderNumber',
      key: 'tenderNumber',
      render: (tenderNumber, record) => (
        <Link
          onClick={() =>
            navigate(`/tender/view-tender/${record.id}`, {
              state: {
                tenderId: record.id,
                tenderNumber: record.tenderNumber,
                screenName: 'Dashboard - Live Tenders with Low Responses',
                status: record.urgencyFlag,
              },
            })
          }
        >
          {tenderNumber}
        </Link>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Tender For',
      dataIndex: 'tenderFor',
      key: 'tenderFor',
      render: (val) => (
        <Tag color={val === 'Material' ? 'purple' : 'cyan'}>
          {val}
        </Tag>
      ),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
    },
    {
      title: 'Responses',
      dataIndex: 'responses',
      key: 'responses',
      render: (val) => <Link>{val}</Link>,
    },
    {
      title: 'Days to Close',
      dataIndex: 'daysToClose',
      key: 'daysToClose',
      render: (_val, record) => getDaysToCloseTag(record),
    },
  ];

  return (
    <Card
      title={
        <div style={{ padding: '10px 0' }}>
          <Title level={5} style={{ marginBottom: 0, color: '#004293' }}>
            Live Tenders with Low Responses
          </Title>
          <Text type="secondary" style={{ fontSize: 12, color: '#8094ae' }}>
            Monitoring tenders nearing closure with fewer than 5 bidder responses
          </Text>
        </div>
      }
      extra={
        <Space>
          <Button
            size="small"
            type="link"
            style={{ fontWeight: 500 }}
            onClick={() => navigate('/tender/tender-list')}
          >
            View All
          </Button>
          <ReloadOutlined onClick={fetchData} style={{ cursor: 'pointer' }} />
        </Space>
      }
      bodyStyle={{ padding: 12 }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin tip="Loading..." />
        </div>
      ) : !data || data.length === 0 ? (
        <Empty description="No live tenders with low responses" />
      ) : (
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          pagination={false}
          size="middle"
          bordered={false}
          className="striped-table"
        />
      )}
    </Card>
  );
};

export default LiveTendersLowResponses;
